import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AlertService } from "../alert/service/alert.service";
import { PaymentErrorHandlerService } from "../services/payment-error-handler.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class RestHttpErrorsInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) { }

  private get alertService(): AlertService {
    return this.injector.get(AlertService);
  }

  private get paymentErrorHandler(): PaymentErrorHandlerService {
    return this.injector.get(PaymentErrorHandlerService);
  }

  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.handleHttpResponse(event, req);
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error, req)),
    );
  }

  private isPaymentRequest(url: string): boolean {
    return (
      url.includes("/v1/api/ui/authorizations") ||
      url.includes("/v1/api/ui/payment-links") ||
      url.includes("/v1/api/ui/payment-operations") ||
      url.includes("/v1/api/ui/payment-link") ||
      url.includes("/v1/api/ui/company-payment-config") ||
      url.includes("/v1/api/ui/payment-simulator")
    );
  }

  private handleHttpResponse(
    event: HttpResponse<any>,
    req: HttpRequest<any>,
  ): void {
    // const { body, status } = event;

    // const bodyStatus = body?.status;
    // // const isBodyStatusError =
    // //   bodyStatus && bodyStatus !== "200" && bodyStatus !== 200;

    //   const isBodyStatusError = bodyStatus && (parseInt(bodyStatus) >= 400);

    // if (status !== 200 || isBodyStatusError) {
    //   if (this.isPaymentRequest(req.url)) {
    //     this.paymentErrorHandler.handleApiResponseError(body);
    //   } else {
    //     const message =
    //       body?.message || "Something went wrong with the request.";
    //     this.alertService.showError(message);
    //   }
    // }

    const { body, status } = event;

    // 1. Check if the HTTP status is in the success range (200-299)
    const isHttpSuccess = status >= 200 && status < 300;

    // 2. Check the internal body status only if it exists
    // We only care if the backend explicitly sent a failure code (e.g., 400, 500)
    const bodyStatus = body?.status;
    const isBodyStatusError = bodyStatus && (parseInt(bodyStatus) >= 400);

    // 3. If the HTTP call failed OR the backend sent an explicit error body
    if (!isHttpSuccess || isBodyStatusError) {
      if (this.isPaymentRequest(req.url)) {
        this.paymentErrorHandler.handleApiResponseError(body);
      } else {
        const message = body?.message || this.translateService.instant("APP_GENERIC_ALERTS.SOMETHING_WENT_WRONG");
        this.alertService.showError(message);
      }
    }

  }

  private handleHttpError(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
  ): Observable<never> {
    if (this.isPaymentRequest(req.url)) {
      this.paymentErrorHandler.handleError(error);
      console.error("HTTP Error: ", error);
      return throwError(() => error);
    }

    let errorMessage = "";
    if (error.error) {
      if (typeof error.error === "string") {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      } else {
        errorMessage = JSON.stringify(error.error);
      }
      if (error.status === 0) {
        errorMessage =
          "La connexion au serveur a été perdue. Veuillez réessayer plus tard.";
      }
    } else {
      errorMessage = this.getServerErrorMessage(error);
    }

    this.alertService.showError(errorMessage);
    console.error("HTTP Error: ", error);
    return throwError(() => new Error(errorMessage));
  }

  private getServerErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return "Bad Request (400). Please check your input.";
      case 401:
        return "Unauthorized (401). Please login again.";
      case 403:
        return "Forbidden (403). You do not have permission to access this resource.";
      case 404:
        return "Not Found (404). The requested resource does not exist.";
      case 500:
        return "Internal Server Error (500). Please try again later.";
      default:
        return `Error ${error.status}: ${error.message || "Unexpected error occurred."}`;
    }
  }
}
